/**
 * Boot Sequence — 최초 진입 시 뜨는 부팅 로그 + 미션 선택 화면.
 * God's Eye View 의 first-run 미션 패널 개념을 DetectX 데이터셋에 맞춰 재구성.
 */
import { type FC, useEffect, useState } from 'react';
import { useAppStore } from '@/stores/app-store';
import type { SensorMode } from '@/components/map/sensor-optics';
import type { RegionPreset } from '@/types';

interface Mission {
  id: string;
  code: string;
  title: string;
  blurb: string;
  accent: string;
  region: RegionPreset;
  sensor: SensorMode;
  layers: string[];
}

const MISSIONS: Mission[] = [
  {
    id: 'contacts',
    code: 'M-01',
    title: 'Live Contacts',
    blurb: 'Aircraft, military flights, naval groups and live traffic cameras streaming onto the globe.',
    accent: '#21c6db',
    region: 'global',
    sensor: 'none',
    layers: ['aircraft', 'mil-aircraft', 'ships', 'naval', 'carriers', 'chokepoints', 'cities', 'traffic-cctv'],
  },
  {
    id: 'crisis',
    code: 'M-02',
    title: 'Crisis Watch',
    blurb: 'Active war zones, ACLED conflict events, missile tracks and drone strikes under thermal optics.',
    accent: '#ffb72d',
    region: 'global',
    sensor: 'flir',
    layers: ['war-zones', 'conflicts', 'acled', 'missiles', 'drone-attacks', 'troop-movements', 'nuclear'],
  },
  {
    id: 'asia',
    code: 'M-03',
    title: 'Asia Deep Dive',
    blurb: 'Indo-Pacific theatre — fleets, chokepoints, semiconductor nodes and regional escalation signals.',
    accent: '#8ff7c7',
    region: 'asia',
    sensor: 'none',
    layers: ['war-zones', 'fighters', 'naval', 'carriers', 'chokepoints', 'submarine-cables', 'cities', 'acled'],
  },
  {
    id: 'explore',
    code: 'M-04',
    title: 'Explore Manually',
    blurb: 'Open the globe with the default layer set and drive it yourself.',
    accent: '#94a3b8',
    region: 'global',
    sensor: 'none',
    layers: ['earthquakes', 'wildfires', 'war-zones', 'missiles', 'troop-movements', 'acled', 'traffic-cctv'],
  },
];

const BOOT_LINES = [
  'QUASAR ENGINE V1 — cold start',
  'binding WebGL2 context ......... OK',
  'mounting globe view ............ OK',
  'NASA EOSDIS GIBS imagery ....... OK',
  'USGS seismic feed .............. OK',
  'NASA FIRMS thermal anomalies ... OK',
  'ACLED conflict events .......... OK',
  'ADS-B contact stream ........... OK',
  'GDELT / RSS ingest ............. OK',
  'sensor optics pipeline ......... READY',
];

export const BootSequence: FC = () => {
  const bootComplete  = useAppStore((s) => s.bootComplete);
  const completeBoot  = useAppStore((s) => s.completeBoot);
  const setLayers     = useAppStore((s) => s.setLayers);
  const setRegion     = useAppStore((s) => s.setRegion);
  const setSensorMode = useAppStore((s) => s.setSensorMode);

  const [shown, setShown] = useState(0);
  const [leaving, setLeaving] = useState(false);

  // 부팅 로그를 한 줄씩 흘린다
  useEffect(() => {
    if (bootComplete || shown >= BOOT_LINES.length) return;
    const t = setTimeout(() => setShown((n) => n + 1), shown === 0 ? 120 : 70);
    return () => clearTimeout(t);
  }, [shown, bootComplete]);

  // ESC = 수동 탐색으로 바로 진입
  useEffect(() => {
    if (bootComplete) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') launch(MISSIONS[3]); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootComplete]);

  if (bootComplete) return null;

  function launch(m: Mission) {
    setLayers(m.layers);
    setRegion(m.region);
    setSensorMode(m.sensor);
    setLeaving(true);
    setTimeout(() => completeBoot(), 420);
  }

  const ready = shown >= BOOT_LINES.length;

  return (
    <div
      className="fixed inset-0 z-[200] bg-surface-deep flex items-center justify-center px-5 transition-opacity duration-500"
      style={{ opacity: leaving ? 0 : 1 }}
    >
      {/* 배경 그리드 + 비네트 */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(33,198,219,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(33,198,219,0.6) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(5,8,12,0.9) 100%)' }}
      />
      <div className="scanlines absolute inset-0 pointer-events-none" />

      <div className="relative w-full max-w-5xl grid md:grid-cols-[minmax(0,320px)_1fr] gap-8 md:gap-10 items-start">
        {/* ── 좌: 부팅 로그 ─────────────────────────────── */}
        <div className="hud-corners px-4 py-4">
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-[17px] font-semibold tracking-[0.24em] text-neon-cyan">DETECTX</span>
            <span className="text-[17px] font-semibold tracking-[0.24em] text-slate-500">QUASAR</span>
          </div>
          <div className="font-mono text-[10px] leading-[1.7] text-slate-500 min-h-[170px]">
            {BOOT_LINES.slice(0, shown).map((l, i) => (
              <div key={i} className={i === shown - 1 && !ready ? 'terminal-cursor' : undefined}>
                <span className="text-slate-700 mr-1.5">›</span>
                <span className={l.endsWith('READY') ? 'text-neon-mint' : undefined}>{l}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 h-px bg-surface-border" />
          <div className="mt-2 font-mono text-[9px] tracking-[0.18em] text-slate-600">
            {ready ? 'SELECT MISSION PROFILE' : 'INITIALISING…'}
          </div>
        </div>

        {/* ── 우: 미션 카드 ─────────────────────────────── */}
        <div
          className="grid sm:grid-cols-2 gap-3 transition-opacity duration-500"
          style={{ opacity: ready ? 1 : 0.5, pointerEvents: ready ? 'auto' : 'none' }}
        >
          {MISSIONS.map((m) => (
            <button
              key={m.id}
              onClick={() => launch(m)}
              className="group text-left glass-panel hud-corners px-4 py-3.5 transition-all duration-200 hover:-translate-y-0.5"
              style={{ boxShadow: `inset 0 0 0 1px ${m.accent}22` }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `inset 0 0 0 1px ${m.accent}77, 0 0 22px ${m.accent}22`; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = `inset 0 0 0 1px ${m.accent}22`; }}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <span className="font-mono text-[9px] tracking-[0.2em]" style={{ color: m.accent }}>{m.code}</span>
                <span className="h-px flex-1" style={{ background: `${m.accent}33` }} />
              </div>
              <div className="text-[13px] font-semibold text-slate-100 mb-1">{m.title}</div>
              <p className="text-[10.5px] leading-[1.55] text-slate-500">{m.blurb}</p>
              <div
                className="mt-2.5 font-mono text-[9px] tracking-[0.16em] opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ color: m.accent }}
              >
                ENGAGE →
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* 하단 힌트 */}
      <div className="absolute bottom-5 left-0 right-0 text-center font-mono text-[9px] tracking-[0.18em] text-slate-700">
        ESC — SKIP  ·  1-6 SENSOR OPTICS  ·  G GLOBE  ·  H HUD
      </div>
    </div>
  );
};
